import { useEffect, useState, useCallback } from 'react';

export interface UseLastUpdatedTimerResult {
  lastUpdateMessage: string;
  resetTimer: () => void;
}

const formatMessage = (seconds: number): string => {
  if (seconds < 60) return 'Last updated a few seconds ago';
  const minutes = Math.floor(seconds / 60);
  return `Last updated ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
};

/**
 * Tracks elapsed time since the last reset and returns a human-readable
 * "Last updated …" message that ticks every second.
 *
 * @param resetKey - Changing this value resets the timer to 0. Prefer a
 * stable primitive (e.g. a timestamp or fetch count) over array/object
 * references — a fresh reference on every render (as with a newly-fetched
 * array) resets the timer on each data update.
 */
const useLastUpdatedTimer = (resetKey?: unknown): UseLastUpdatedTimerResult => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setSeconds(0);
  }, [resetKey]);

  const resetTimer = useCallback(() => setSeconds(0), []);

  return { lastUpdateMessage: formatMessage(seconds), resetTimer };
};

export default useLastUpdatedTimer;
